import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { IOfflineDevice, IOfflineTag, IOfflineTankBattery } from "../offline-data-service/Models";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import restService from "../rest-service/RestService";
import { IDynaCardViewState, IGroupViewState, InternalGroupViewState, InternalObjectViewState, InternalTagViewState, IObjectViewState, ITagViewState, IUIRefreshInterval } from "./Models";




const StateDataService = {


    getAutoRefreshIntervals: async function (signal?: AbortSignal): Promise<IUIRefreshInterval[]> {

        const response = await restService.get<IUIRefreshInterval[]>(`/autorefresh-intervals`, signal);
        return response.data;

    },

    getObjectViewState: async function (
        customerID: number,
        groupID: number,
        eLynxObject: IeLynxObject,
        offline: boolean,
        signal?: AbortSignal
    ): Promise<IGroupViewState | IObjectViewState | ITagViewState> {

        if (!offline) {

            const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
                `/groups/${groupID}/summary-listing/state-data` :
                `/groups/${groupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/state-data`;

            switch (eLynxObject.objectType) {

                case eLynxObjectTypeEnum.Group:
                    const response = await restService.get<InternalGroupViewState>(path, signal);
                    return {
                        contextObject: {
                            id: eLynxObject.id,
                            objectType: eLynxObject.objectType,
                            objectName: response.data.groupName,  
                            displayText: response.data.groupName,
                        },
                        features: response.data.features,
                        refreshSeconds: response.data.refreshSeconds,
                        defaultConfigurationTATs: response.data.defaultConfigurationTATs,
                        accessPermissions: response.data.accessPermissions,
                        isSystemBalance: response.data.isSystemBalance,
                        organizationID: response.data.organizationID,
                        summaryListingSortType: response.data.summaryListingSortType
                    };

                case eLynxObjectTypeEnum.Device:
                case eLynxObjectTypeEnum.Tank:
                case eLynxObjectTypeEnum.Well:
                case eLynxObjectTypeEnum.TankBattery:
                    const objectResponse = await restService.get<InternalObjectViewState>(path, signal);
                    return {
                        contextObject: {
                            id: eLynxObject.id,
                            objectType: eLynxObject.objectType,
                            objectName: objectResponse.data.name,
                            displayText: objectResponse.data.name
                        },
                        features: objectResponse.data.features,
                        features2: objectResponse.data.features2,
                        deviceType: objectResponse.data.deviceType,
                        accessPermissions: objectResponse.data.accessPermissions,
                        externalContent: objectResponse.data.externalContent,
                        linkedDeviceIDs: objectResponse.data.linkedDeviceIDs,
                        hybridServicesR1Enabled: objectResponse.data.hybridServicesR1Enabled,
                        platformObjectType: objectResponse.data.platformObjectType
                        
                        
                    };

                case eLynxObjectTypeEnum.Tag:
                    const tagResponse = await restService.get<InternalTagViewState>(path, signal);
                    return {
                        contextObject: {
                            id: eLynxObject.id,
                            objectType: eLynxObject.objectType,
                            objectName: tagResponse.data.tagName,
                            displayText: tagResponse.data.tagName
                        },
                        tagObjectName: tagResponse.data.tagObjectName,
                        features: tagResponse.data.features,
                        historyRowCount: tagResponse.data.historyRowCount,
                        refreshSeconds: tagResponse.data.refreshSeconds,
                        tagObjectType: tagResponse.data.tagObjectType,
                        tagType: tagResponse.data.tagType,
                        tagUnits: tagResponse.data.tagUnits,
                        dataType: tagResponse.data.dataType,
                        defaultMaxRowCount: tagResponse.data.defaultMaxRowCount,
                        uomID: tagResponse.data.uomID
                    } as ITagViewState;

                default:
                    //throw new Error(`getObjectDetailsData not implemented for ${eLynxObject.objectType}`);
                    return {
                    } as IObjectViewState;
            }
        } else {
            switch (eLynxObject.objectType) {
                case eLynxObjectTypeEnum.Group:

                    const p1 = await OfflineDataService.getGroup(customerID, groupID);
                    const p2 = OfflineDataService.getGroupDevices(customerID, groupID, true, ObjectFeaturesEnum.FormModify);
                    const p3 = OfflineDataService.getGroupWells(customerID, groupID, ObjectFeaturesEnum.FormModify);
                    const p4 = OfflineDataService.getGroupTankBatteries(customerID, groupID, ObjectFeaturesEnum.FormModify);
        
                    let [groupData, devices, wells, tankBatteries] = await Promise.all([p1, p2, p3, p4]);

                    const formModify = devices.length > 0 || wells.length > 0 || tankBatteries.length > 0;

                    return {
                        contextObject: {
                            id: eLynxObject.id,
                            objectType: eLynxObject.objectType,
                            objectName: groupData?.title as string
                        },
                        refreshSeconds: 0,
                        accessPermissions: formModify ? [{
                            functionalityType: AccessFunctionalityTypeEnum.FormInstance,
                            access: (AccessTypeEnum.Read_View | AccessTypeEnum.Write_Change)
                        }] : undefined
                    } as IGroupViewState;

                case eLynxObjectTypeEnum.Tag: 
                case eLynxObjectTypeEnum.Device: 
                case eLynxObjectTypeEnum.Tank:
                case eLynxObjectTypeEnum.TankBattery: {

                    const offlineObject = (await OfflineDataService.getObject(customerID, eLynxObject)) as IOfflineDevice | IOfflineTankBattery | IOfflineTag | undefined;

                    const formModify = offlineObject ? (offlineObject.features & ObjectFeaturesEnum.FormModify) > 0 : false;

                    return {
                        contextObject: {
                            id: eLynxObject.id,
                            objectType: eLynxObject.objectType,
                            objectName: offlineObject?.name ?? ""
                        },
                        features: offlineObject?.features,
                        accessPermissions: formModify ? [{
                            functionalityType: AccessFunctionalityTypeEnum.FormInstance,
                            access: (AccessTypeEnum.Read_View | AccessTypeEnum.Write_Change)
                        }] : undefined

                    } as IObjectViewState;
                }

                case eLynxObjectTypeEnum.Well: {

                    const offlineObject = (await OfflineDataService.getObject(customerID, eLynxObject));

                    const formModify = offlineObject ? (offlineObject.features & ObjectFeaturesEnum.FormModify) > 0 : false;

                    return {
                        contextObject: {
                            id: eLynxObject.id,
                            objectType: eLynxObject.objectType,
                            objectName: offlineObject?.name ?? ""
                        },
                        accessPermissions: formModify ? [{
                            functionalityType: AccessFunctionalityTypeEnum.FormInstance,
                            access: (AccessTypeEnum.Read_View | AccessTypeEnum.Write_Change)
                        }] : undefined
                    } as IObjectViewState;
                }

                default:
                    throw new Error(`getObjectViewState not implemented for ${eLynxObject.objectType}`);
            }
        }
    },
    getDynacardViewState: async function(contextGroupID: number, deviceID: number, signal?: AbortSignal): Promise<IDynaCardViewState> {

        const path =`/groups/${contextGroupID}/devices/${deviceID}/dynacard/state-data`;
        const response = await restService.get<IDynaCardViewState>(path, signal);
        return response.data;
    },
    // getPlungerAnalysisState: async function(contextGroupID: number, deviceID: number, signal?: AbortSignal): Promise<IDynaCardViewState> {

    //     const path =`/groups/${contextGroupID}/devices/${deviceID}/plungeranalysis/state-data`;
    //     const response = await restService.get<IDynaCardViewState>(path, signal);
    //     return response.data;
    // }
};

export default StateDataService;